// SB6-ANA-FILT-06 (Track D / D9) — filter-config blob shapes + validity
// gating.
//
// The server stores an opaque `config` JSON blob per saved config (named
// preset or last-used); the FE owns its interpretation. This module
// defines the two per-kind blob shapes and the pure functions that
// (a) build a blob from the live widget state, and (b) RESTORE a blob into
// the widget state, **validity-gated** (D9):
//
//   * an option (AI class / defect type) is dropped from the restored
//     selection when it no longer has data in the current facets (count 0
//     or absent), and
//   * the whole blob is ignored when its `modelSignature` differs from the
//     current DA's (the loader checks this before calling restore).
//
// Never resets on its own otherwise — exactly the 05 global-filter
// persistence behaviour, but via the filter-config endpoints rather than
// LysePreferences.
import {
  clampConfidencePct,
  DEFAULT_AI_MIN_CONFIDENCE_PCT,
  type AiClassConfig,
} from "./state/aiFilterStore.js";

// --- AI config blob --------------------------------------------------------

/** Persisted shape for the AI Filter Module config blob. */
export interface AiConfigBlob {
  /** Whether the AI sub-filter is active (mirrors L3 AI kind selection). */
  active: boolean;
  /** Per-class { checked, minPct } map, keyed by `class_uuid`. */
  classes: Record<string, AiClassConfig>;
}

/** Build the AI config blob from the live widget state. */
export function buildAiConfigBlob(
  active: boolean,
  config: Readonly<Record<string, AiClassConfig>>,
): AiConfigBlob {
  const classes: Record<string, AiClassConfig> = {};
  for (const [uuid, row] of Object.entries(config)) {
    classes[uuid] = {
      checked: row.checked,
      minPct: clampConfidencePct(row.minPct),
    };
  }
  return { active, classes };
}

/**
 * Parse an opaque server blob into a typed {@link AiConfigBlob}, tolerating
 * partial/garbage shapes (defensive — the blob is `additionalProperties`).
 */
export function parseAiConfigBlob(
  raw: Readonly<Record<string, unknown>>,
): AiConfigBlob {
  const active = raw.active === true;
  const classes: Record<string, AiClassConfig> = {};
  const rawClasses = raw.classes;
  if (rawClasses && typeof rawClasses === "object") {
    for (const [uuid, v] of Object.entries(
      rawClasses as Record<string, unknown>,
    )) {
      if (!v || typeof v !== "object") continue;
      const row = v as { checked?: unknown; minPct?: unknown };
      classes[uuid] = {
        checked: row.checked === true,
        minPct:
          typeof row.minPct === "number"
            ? clampConfidencePct(row.minPct)
            : DEFAULT_AI_MIN_CONFIDENCE_PCT,
      };
    }
  }
  return { active, classes };
}

/**
 * Validity-gate an AI blob against the set of class uuids that currently
 * have data (`presentClassUuids`). A checked class with no data is dropped
 * from the restored selection (D9) — it is simply unchecked, so the user
 * never gets a phantom filter narrowing the set to nothing. Classes absent
 * from the blob keep their default (unchecked) downstream.
 */
export function gateAiConfigBlob(
  blob: AiConfigBlob,
  presentClassUuids: ReadonlySet<string>,
): Record<string, AiClassConfig> {
  const out: Record<string, AiClassConfig> = {};
  for (const [uuid, row] of Object.entries(blob.classes)) {
    const present = presentClassUuids.has(uuid);
    out[uuid] = {
      // Drop the check when the class no longer has data.
      checked: row.checked && present,
      minPct: clampConfidencePct(row.minPct),
    };
  }
  return out;
}

// --- Defect config blob ----------------------------------------------------

/** Persisted shape for the Defect Filter Widget config blob. */
export interface DefectConfigBlob {
  /** Whether the defect sub-filter is active (mirrors L3 Defect kind). */
  active: boolean;
  /** Checked defect catalogue-entry ids (drives `defectTypeIds`). */
  defectTypeIds: string[];
}

/** Build the Defect config blob from the live widget state. */
export function buildDefectConfigBlob(
  active: boolean,
  defectTypeIds: readonly string[],
): DefectConfigBlob {
  return { active, defectTypeIds: [...defectTypeIds] };
}

/** Parse an opaque server blob into a typed {@link DefectConfigBlob}. */
export function parseDefectConfigBlob(
  raw: Readonly<Record<string, unknown>>,
): DefectConfigBlob {
  const active = raw.active === true;
  const ids = Array.isArray(raw.defectTypeIds)
    ? raw.defectTypeIds.filter((v): v is string => typeof v === "string")
    : [];
  return { active, defectTypeIds: ids };
}

/**
 * Validity-gate a Defect blob against the set of catalogue-entry ids that
 * currently have data (`presentTypeIds`). A checked type with no data is
 * dropped from the restored selection (D9).
 */
export function gateDefectConfigBlob(
  blob: DefectConfigBlob,
  presentTypeIds: ReadonlySet<string>,
): string[] {
  return blob.defectTypeIds.filter((id) => presentTypeIds.has(id));
}

// --- Defect model signature (D9) -------------------------------------------

/**
 * Deterministic defect model signature.
 *
 * AI uses the `modelSignature` the BE returns from `getAiClassFacets`. The
 * defect side has no equivalent single field, so we derive one from the
 * defect facets response + the DA's active catalogue:
 *
 *   `defect:<classModelId|none>:<catalogueId|nocat>`
 *
 * Rationale: a defect config's validity hinges on (a) the **defect class
 * model** (which severity grades exist — `classModelId`, `none` when the DA
 * uses the None model so `hasClassModel` is false) and (b) the **catalogue**
 * that supplies the defect types the user checked. Two DAs with the same
 * class model AND the same catalogue share the same checkable type universe,
 * so a preset transfers; differ on either and the preset must not load (D9).
 * The signature is a pure function of those two ids so it is stable and
 * reproducible.
 */
export function deriveDefectModelSignature(args: {
  classModelId: string | null;
  catalogueId: string | null;
}): string {
  const model = args.classModelId ?? "none";
  const cat = args.catalogueId ?? "nocat";
  return `defect:${model}:${cat}`;
}
